import MomentUtils from '@date-io/moment';
import Framework, { LoadingHandler, shapes } from '@greenville/framework';
import { Box, Button, Grid, Paper, Typography } from '@material-ui/core';
import { ThemeProvider, createTheme, withStyles } from '@material-ui/core/styles';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import axios from 'axios';
import * as _ from 'lodash';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import Loader from '../../../common/Loader';
import ChatgptConfig from '../../../common/config/ChatgptConfig';
import * as constants from '../../../common/constants';
import env from '../../../common/env';
import utils from '../../../common/utils';
import AutocompleteComponent from '../common/components/Autocomplete';
import AIChartsUtilityModel from '../models/AIChartsUtility';
import AIContentFilterChartsModel from '../models/AIContentFilterCharts';
import AIContentFilterChartsDetailsModel from '../models/AIContentFilterChartsDetails';
import AIFeedbackChartsDetailsModel from '../models/AIFeedbackChartsDetails';
import CDNToken from '../models/CDNToken';
import InternalUsersList from '../models/InternalUsersList';
import Listing from '../models/Listing';
import McqSearch from '../models/McqSearch';
import TenantConfigModel from '../models/TenantConfig';
import Trends from '../models/Trends';
import AIChartsComponent from './aiCharts/AIChartsComponent';
import AIContentFilterCharts from './aiCharts/contentFilterCharts/AIContentFilterCharts';
import ChatTrendsGrid from './datagrid/ChatTrendsGrid';

const materialTheme = createTheme({
  overrides: {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: constants.PEARSON_PRIMARY_COLOR
      }
    },
    MuiPickersYear: {
      root: {
        '&:hover': {
          backgroundColor: '#d2cfc4'
        }
      },
      yearSelected: {
        color: constants.PEARSON_PRIMARY_COLOR
      },
      current: {
        color: constants.PEARSON_PRIMARY_COLOR
      }
    },
    MuiPickersDay: {
      day: {
        '&:hover': {
          backgroundColor: '#d2cfc4'
        }
      },
      daySelected: {
        backgroundColor: constants.PEARSON_PRIMARY_COLOR,
        '&:hover': {
          backgroundColor: constants.PEARSON_PRIMARY_COLOR
        }
      },
      current: {
        color: constants.PEARSON_PRIMARY_COLOR
      }
    }
  }
});

const themes = createTheme({
  palette: {
    primary: {
      light: '#047a9c',
      main: '#005d83',
      dark: '#003558',
      contrastText: '#ffffff'
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 30
      }
    }
  }
});

const styles = (theme) => ({
  buttonGroupStyle: {
    justifyContent: 'center'
  },
  datePicker: {
    marginTop: 'inherit'
  },
  marginBox: {
    border: '1px solid #322c2c2e',
    width: '52%',
    padding: '2px 15px 2px 0px',
    borderRadius: '4px',
    color: '#fff',
    height: '41px'
  },
  blurStyle: {
    position: 'absolute',
    background: 'rgb(255 255 255 / 20%)',
    top: '0',
    width: '93%',
    height: '100%',
    backdropFilter: 'blur(0.3px)',
    zIndex: '9999'
  },
  buttonStyle: {
    width: '120px',
    margin: '10px'
  },
  container: {
    marginBottom: 20
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    color: theme.palette.text.secondary
  },
  title: {
    marginTop: -20
  },
  labelWidht: {
    width: '380px'
  },
  boxWidth: {
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(4)
  },
  button: {
    paddingTop: theme.spacing(2),
    marginBottom: '-3em'
  },
  root: {
    top: '12%'
  },
  msgStyle: {
    fontSize: 'large',
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(0)
    },
    zIndex: 9999
  }
});

@inject(
  'chatgptTrendsUtility',
  'chatgptListingUtility',
  'chatgptUtilityTrendsStatus',
  'ChatGptUtilityListingStatus',
  'mcqSearch',
  'aiChartsUtility',
  'aiContentfilterCharts',
  'aiContentfilterChartsDetails',
  'aiFeedbackChartsDetails',
  'AIChartsUtilityService',
  'AIContentFilterChartsService',
  'aiFeedbackChartsDetailsListService',
  'AIContentFilterChartsDetailsService',
  'internalUsers',
  'cdnToken',
  'tenantConfig'
)
@observer
class ChatGptUtilityTrends extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    chatgptTrendsUtility: shapes.modelOf(Trends).isRequired,
    chatgptListingUtility: shapes.modelOf(Listing).isRequired,
    chatgptUtilityTrendsStatus: shapes.state.isRequired,
    ChatGptUtilityListingStatus: shapes.state.isRequired,
    mcqSearch: shapes.modelOf(McqSearch).isRequired,
    aiChartsUtility: shapes.modelOf(AIChartsUtilityModel).isRequired,
    aiContentfilterCharts: shapes.modelOf(AIContentFilterChartsModel).isRequired,
    aiContentfilterChartsDetails: shapes.modelOf(AIContentFilterChartsDetailsModel).isRequired,
    aiFeedbackChartsDetails: shapes.modelOf(AIFeedbackChartsDetailsModel).isRequired,
    AIChartsUtilityService: shapes.modelOf(AIChartsUtilityModel).isRequired,
    AIContentFilterChartsService: shapes.modelOf(AIContentFilterChartsModel).isRequired,
    aiFeedbackChartsDetailsListService: shapes.modelOf(AIFeedbackChartsDetailsModel).isRequired,
    AIContentFilterChartsDetailsService: shapes.modelOf(AIContentFilterChartsDetailsModel).isRequired,
    internalUsers: shapes.modelOf(InternalUsersList).isRequired,
    cdnToken: shapes.modelOf(CDNToken).isRequired,
    tenantConfig: shapes.modelOf(TenantConfigModel).isRequired,
    history: PropTypes.object.isRequired
  };
  /* eslint-disable */
  constructor(props) {
    super(props);
    const { chatgptTrendsUtility, chatgptListingUtility, ChatGptUtilityListingStatus } = props;
    this.defaultSearch = {
      bookId: '',
      startDate: '',
      endDate: ''
    };
    this.state = {
      tenantId: '',
      tenantName: '',
      tenantTitle: '',
      newRequest: true,
      searchFields: '',
      gptResponse: chatgptTrendsUtility,
      gptListingResponse: {
        chatListDatas: chatgptListingUtility,
        chatListStatus: ChatGptUtilityListingStatus,
        loaderStatusResponse: true
      },
      endDisabled: true,
      loaderStatus: true,
      showDateFields: false,
      showOutline: constants.CLUSTER_ONE_DAY_VALUE,
      setOrder: 'ASC',
      setDirectionValue: '',
      selectRadioOption: false,
      selectListingOption: false,
      toggleBtnValue: 'cluster',
      bookList: [],
      inputValue: '',
      tokenValue: '',
      showCharts: {
        userFeedBackChart: false,
        blockedCountChart: false
      },
      defaultDropdownValue: { title: constants.ALL_BOOKS, bookId: constants.ALL_BOOKS, id: 1 },
      isSearchClicked: false,
      isUserIdExcluded: true,
      startDateError: false,
      endDateError: false
    };
  }

  setTenantDetails = () => {
    const { history, tenantConfig } = this.props;
    const selectedTabName = history.location.pathname.split('/');
    ChatgptConfig.tenantData.forEach((item) => {
      if (item.name === selectedTabName[2]) {
        this.setState({
          tenantId: utils.getTenantId(item.name, tenantConfig),
          tenantName: item.name,
          tenantTitle: item.title
        });
      }
    });
  };

  componentDidMount = () => {
    this.setTenantDetails();
    const { chatgptTrendsUtility, mcqSearch } = this.props;
    if (mcqSearch && mcqSearch.data.length > 0) {
      const resData = _.cloneDeep(toJS(mcqSearch.data));
      resData.unshift({ title: constants.ALL_BOOKS, bookId: constants.ALL_BOOKS, id: 1 });
      this.setState({
        bookList: resData,
        searchFields: { bookId: constants.ALL_BOOKS }
      });
    }
    /* eslint-disable camelcase */
    const temp = window.location.pathname;
    if (temp === '/chatgptutility/aistudytools/trends') {
      this.setState({
        selectRadioOption: false,
        selectListingOption: false
      });
    }

    Framework.getEventManager().on(constants.GET_CHATGPT_LISTING_MODEL_DATA, () => {
      const { chatgptListingUtility } = this.props;
      const { gptListingResponse } = this.state;
      this.setState({
        gptListingResponse: {
          ...gptListingResponse,
          chatListDatas: chatgptListingUtility
        }
      });
    });

    Framework.getEventManager().on(constants.GET_CHATGPT_TRENDS_MODEL_DATA, () => {
      const { data } = chatgptTrendsUtility;
      if (chatgptTrendsUtility) {
        const { newRequest, gptResponse, bookList } = this.state;
        const combineData = [...data];
        bookList.forEach((item) => {
          const alteredValue = item.bookId;
          if (!alteredValue) return;
          combineData.forEach((dataValue, index) => {
            if (dataValue.bookId === alteredValue) {
              combineData[index].title = item.title;
            }
          });
        });
        const combineDatatoJS = JSON.parse(JSON.stringify(toJS(combineData)));
        /* eslint-enable camelcase */
        if (newRequest) {
          const sortedDate = combineDatatoJS.sort((x, y) => new Date(y.date) - new Date(x.date));
          this.setState({ gptResponse: sortedDate });
        } else {
          const combineDataGpt = [...gptResponse, ...combineDatatoJS];
          const sortedDate = combineDataGpt.sort((x, y) => new Date(y.date) - new Date(x.date));
          this.setState((prev) => ({ gptResponse: [...prev.gptResponse, ...sortedDate] }));
        }
      }
    });
    /* eslint-enable camelcase */
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { mcqSearch, internalUsers, cdnToken } = this.props;
    const { tokenValue } = this.state;
    const usersData = JSON.parse(JSON.stringify(toJS(internalUsers)));
    const cdnTokenValue = JSON.parse(JSON.stringify(toJS(cdnToken)));
    const isCDNtokenPresent = utils.getCookieByName('etext-cdn-token');
    if (prevState.tokenValue !== tokenValue) {
      if (_.isEmpty(cdnTokenValue?.name) && !isCDNtokenPresent) {
        this.getCDNTokenData();
      }
      if (mcqSearch && mcqSearch.data.length === 0) {
        this.getBookData();
      }
      if (usersData && usersData?.userId.length === 0) {
        this.getS3InternalUsersList();
      }
    }
  };

  getCDNTokenData = async () => {
    const { tokenValue } = this.state;
    const headers = {
      Authorization: `Bearer ${tokenValue}`
    };
    const response = await axios.get(`${env.EVERGREEN_API_BASE_URL}${constants.GET_CDN_TOKEN_URL}`, { headers });
    if (response && response.data && response.data.data) {
      const cdnTokenVal = response.data.data;
      Framework.getStoreRegistry().getStore('cdnToken').setResponse(cdnTokenVal);
      utils.setCDNTokenInCookie(cdnTokenVal);
    }
  };

  getS3InternalUsersList = async () => {
    const { tokenValue } = this.state;
    const headers = {
      Authorization: `Bearer ${tokenValue}`
    };
    const payload = {
      itemPath: constants.S3_EXCLUDED_USERS_LIST_PATH
    };
    const response = await axios.post(`${env.EVERGREEN_API_BASE_URL}${constants.GET_S3_USERS_LIST_URL}`, payload, {
      headers
    });
    if (response && response.data && response.data.data) {
      Framework.getStoreRegistry().getStore('internalUsers').setResponse(response.data.data[0]);
    }
  };

  getBookData = async () => {
    const { tokenValue } = this.state;
    const headers = {
      Authorization: `Bearer ${tokenValue}`
    };
    const response = await axios.post(`${env.EVERGREEN_API_BASE_URL}${constants.GET_BOOK_DATA_URL}`, {}, { headers });
    if (response && response.data && response.data.data) {
      const resData = _.cloneDeep(response.data.data);
      resData.unshift({ title: constants.ALL_BOOKS, bookId: constants.ALL_BOOKS, id: 1 });
      Framework.getStoreRegistry().getStore('mcqSearch').setResponse(response.data);
      this.setState({
        bookList: resData,
        searchFields: { bookId: constants.ALL_BOOKS }
      });
    }
  };

  onInputChange = (event, newInputValue) => {
    this.setState({
      inputValue: newInputValue
    });
  };

  handleChange = (e) => {
    const { searchFields } = this.state;
    const fieldChanged = {};
    fieldChanged[e.target.name] = e.target.value;
    this.setState({
      searchFields: { ...searchFields, ...fieldChanged }
    });
  };

  handleToggleBtnChange = (e) => {
    const {
      chatgptListingUtility,
      chatgptTrendsUtility,
      aiChartsUtility,
      aiContentfilterCharts,
      aiContentfilterChartsDetails,
      aiFeedbackChartsDetails
    } = this.props;
    this.setState({
      selectRadioOption: e.target.value,
      toggleBtnValue: e.target.value
    });
    chatgptListingUtility.resetStoreValues();
    chatgptTrendsUtility.resetStoreValues();
    aiChartsUtility.resetStoreValues();
    aiFeedbackChartsDetails.resetStoreValues();
    aiContentfilterCharts.resetStoreValues();
    aiContentfilterChartsDetails.resetStoreValues();
  };

  handleReset = () => {
    const { aiChartsUtility, aiContentfilterCharts, aiContentfilterChartsDetails, aiFeedbackChartsDetails } =
      this.props;
    const { gptListingResponse } = this.state;
    this.setState({
      showOutline: constants.CLUSTER_ONE_DAY_VALUE,
      toggleBtnValue: 'cluster',
      searchFields: {
        ...this.defaultSearch
      },
      gptListingResponse: {
        ...gptListingResponse,
        chatListDatas: []
      },
      inputValue: '',
      isSearchClicked: false,
      showDateFields: false
    });
    aiChartsUtility.resetStoreValues();
    aiFeedbackChartsDetails.resetStoreValues();
    aiContentfilterCharts.resetStoreValues();
    aiContentfilterChartsDetails.resetStoreValues();
  };

  handleAscending = (dir, value) => {
    const { gptResponse } = this.state;
    if (dir === 'asc') {
      const sorted = [...gptResponse].sort((a, b) => (a[value] > b[value] ? 1 : -1));
      this.setState({
        ...gptResponse,
        gptResponse: sorted
      });
    } else {
      const sorted = [...gptResponse].sort((a, b) => (a[value] < b[value] ? 1 : -1));
      this.setState({
        ...gptResponse,
        gptResponse: sorted
      });
    }
  };

  handleSearch = (customPayload) => {
    const { searchFields, toggleBtnValue, startDateError, endDateError, showOutline } = this.state;
    const { chatgptTrendsUtility, chatgptListingUtility, tenantConfig } = this.props;
    const temp = window.location.pathname;
    if (startDateError || endDateError) {
      return;
    }
    if (
      temp === '/chatgptutility/aistudytools/trends/charts' ||
      temp === '/chatgptutility/aistudytools/trends/clustering'
    ) {
      this.setState({
        selectRadioOption: false,
        selectListingOption: true
      });
    } else {
      this.setState({
        selectRadioOption: true,
        selectListingOption: false
      });
    }
    if (customPayload && customPayload.lastKey) {
      this.setState({ newRequest: false });
    } else {
      this.setState({ newRequest: true });
    }
    const payload = { ...searchFields, ...customPayload };
    if (payload.bookId === constants.ALL_BOOKS) {
      delete payload.bookId;
    }
    let endDateChanged = new Date();
    endDateChanged = moment(endDateChanged).format(constants.YYYY_MM_DD_FORMAT);

    if ((payload && payload.startDate) || (payload && payload.bookId)) {
      if (!payload.endDate && payload.startDate) {
        payload.endDate = endDateChanged;
        this.setState({
          searchFields: { ...searchFields, endDate: endDateChanged }
        });
      }
    } else {
      payload.endDate = endDateChanged;
      payload.startDate = endDateChanged;
      this.setState({
        searchFields: { ...searchFields, startDate: endDateChanged, endDate: endDateChanged }
      });
    }
    if (toggleBtnValue === 'cluster') {
      this.setState({
        selectRadioOption: false,
        selectListingOption: true
      });
      delete payload.bookId;
      if (showOutline === constants.CLUSTER_ONE_DAY_VALUE) {
        payload.startDate = endDateChanged;
      }
      if (_.isEmpty(searchFields?.bookId) || searchFields?.bookId === constants.ALL_BOOKS) {
        payload.identifierId = constants.ALL_BOOKS;
      } else {
        payload.identifierId = searchFields?.bookId;
      }
      chatgptListingUtility.fetch({ ...payload, tenantId: utils.getTenantId('aistudytools', tenantConfig) });
    }
    if (toggleBtnValue === 'count') {
      chatgptTrendsUtility.fetch({ ...payload, tenantId: utils.getTenantId('aistudytools', tenantConfig) });
    }
    if (toggleBtnValue === 'userFeedBack') {
      this.setState({
        showCharts: {
          userFeedBackChart: true,
          blockedCountChart: false
        },
        isSearchClicked: true
      });
    }
    if (toggleBtnValue === 'blockedCount') {
      this.setState({
        showCharts: {
          userFeedBackChart: false,
          blockedCountChart: true
        },
        isSearchClicked: true
      });
    }
  };

  trendsDataFetch = (value) => {
    const { showDateFields } = this.state;
    if (value === 'custom') {
      this.setState({
        showOutline: value,
        showDateFields: !showDateFields
      });
    }
  };

  handleDateChange = (date, name) => {
    this.setState((prevState) => {
      const { searchFields } = prevState;
      const fieldChanged = {};
      fieldChanged[name] = moment(date).format(constants.MM_DD_YYYY_FORMAT);

      const newState = {
        [`${name}Error`]: false,
        searchFields: { ...searchFields },
        endDisabled: false
      };

      if (date === null && fieldChanged[name] === 'Invalid date') {
        newState.searchFields[name] = '';
      } else if (fieldChanged[name] === 'Invalid date') {
        newState[`${name}Error`] = true;
        /* eslint-disable-next-line no-underscore-dangle */
        newState.searchFields[name] = date._i;
      } else {
        newState.searchFields[name] = fieldChanged[name];
      }

      if (name === 'startDate' && fieldChanged[name] === 'Invalid date') {
        newState.endDisabled = true;
        newState.searchFields.endDateTime = '';
        newState.endDateError = false;
      }

      return newState;
    });
  };

  handleDateclicker = (event, value) => {
    const { searchFields } = this.state;
    const daysvalue = event.target.innerText;
    let startDate = new Date();
    let endDate = new Date();
    switch (daysvalue) {
      case '1d':
        this.setState({
          showOutline: value,
          showDateFields: false
        });
        break;
      case '3d':
        startDate.setDate(startDate.getDate() - 2);
        this.setState({
          showOutline: value,
          showDateFields: false
        });
        break;
      case '1w':
        startDate.setDate(startDate.getDate() - 6);
        this.setState({
          showOutline: value,
          showDateFields: false
        });
        break;
      default:
    }
    startDate = moment(startDate).format(constants.YYYY_MM_DD_FORMAT);
    endDate = moment(endDate).format(constants.YYYY_MM_DD_FORMAT);
    this.setState({
      searchFields: { ...searchFields, startDate, endDate }
    });
  };

  sorting = (value) => {
    const { setOrder } = this.state;
    if (setOrder === 'ASC') {
      this.handleAscending('asc', value);
      this.setState({
        setOrder: 'DSC',
        setDirectionValue: value
      });
    }
    if (setOrder === 'DSC') {
      this.handleAscending('des', value);
      this.setState({
        setOrder: 'ASC',
        setDirectionValue: value
      });
    }
  };

  onAutoCompleteChange = (event, newValue) => {
    const { searchFields } = this.state;
    this.setState({
      searchFields: {
        ...searchFields,
        bookId: newValue !== null ? newValue.bookId : ''
      }
    });
  };

  handleOnSearch = (param) => {
    this.setState({
      isSearchClicked: param
    });
  };

  handleFilterUserChange = (e) => {
    this.setState({
      isUserIdExcluded: e.target.checked
    });
  };

  render() {
    const {
      classes,
      chatgptUtilityTrendsStatus,
      AIChartsUtilityService,
      AIContentFilterChartsService,
      aiFeedbackChartsDetailsListService,
      AIContentFilterChartsDetailsService,
      tenantConfig
    } = this.props;
    const {
      searchFields,
      endDisabled,
      loaderStatus,
      showDateFields,
      showOutline,
      gptResponse,
      gptListingResponse,
      setOrder,
      setDirectionValue,
      selectRadioOption,
      selectListingOption,
      toggleBtnValue,
      bookList,
      inputValue,
      tokenValue,
      showCharts,
      defaultDropdownValue,
      isSearchClicked,
      isUserIdExcluded,
      tenantName,
      startDateError,
      endDateError
    } = this.state;
    const activeSession = utils.getSessionData();
    if (activeSession !== null && tokenValue === '') {
      const token = utils.getToken();
      this.setState({
        tokenValue: token
      });
    }
    const { chatListStatus } = gptListingResponse;
    return (
      <>
        <Paper>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ p: 3 }}>
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                spacing={9}
                className={classes.title}
                style={{ p: 2, mt: 2 }}
              >
                Please enter search criteria to fetch the details
              </Typography>
            </Box>
            <Grid container direction="row">
              <Grid item xs={2} />
              <Grid item xs={8}>
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', p: '0px 33px' }}>
                  <FormControl sx={{ minWidth: '45%', maxWidth: '90%', mr: 4 }} size="small">
                    <AutocompleteComponent
                      options={bookList}
                      onAutoCompleteChange={this.onAutoCompleteChange}
                      onInputChange={this.onInputChange}
                      stateVal={inputValue}
                      errorVal=""
                      name="bookId"
                      label="Book Id"
                      componentDefaultValue={defaultDropdownValue}
                    />
                  </FormControl>
                  <Box className={classes.marginBox} sx={{ '& button': { m: 31, p: 12 } }}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant={showOutline === constants.CLUSTER_ONE_DAY_VALUE ? 'outlined' : 'text'}
                        style={{ color: '#0d0c0c8c' }}
                        onClick={(event) => this.handleDateclicker(event, constants.CLUSTER_ONE_DAY_VALUE)}
                      >
                        1d
                      </Button>
                      <Button
                        size="small"
                        style={{ color: '#0d0c0c8c' }}
                        variant={showOutline === '3D' ? 'outlined' : 'text'}
                        onClick={(event) => this.handleDateclicker(event, '3D')}
                      >
                        3d
                      </Button>
                      <Button
                        size="small"
                        style={{ color: '#0d0c0c8c' }}
                        variant={showOutline === '1W' ? 'outlined' : 'text'}
                        onClick={(event) => this.handleDateclicker(event, '1W')}
                      >
                        1w
                      </Button>
                      <Button
                        size="small"
                        variant={showOutline === 'custom' ? 'outlined' : 'text'}
                        style={{ color: '#0d0c0c8c' }}
                        onClick={() => this.trendsDataFetch('custom')}
                      >
                        custom
                        <CalendarMonthIcon />
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={2} />
            </Grid>
            <br />
            {showDateFields && (
              <Grid container direction="row">
                <Grid item xs={2} />
                <Grid item xs={8}>
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', p: '0px 33px' }}>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <ThemeProvider theme={materialTheme}>
                        <KeyboardDatePicker
                          className={classes.datePicker}
                          format={constants.MM_DD_YYYY_FORMAT}
                          autoOk
                          size="small"
                          variant="inline"
                          inputVariant="outlined"
                          style={{ minWidth: '32%', marginRight: '6%' }}
                          disableFuture
                          value={searchFields.startDate || null}
                          label="Start Date"
                          onChange={(date) => this.handleDateChange(date, 'startDate')}
                          KeyboardButtonProps={{
                            'aria-label': 'change date'
                          }}
                          helperText={startDateError ? 'Invalid Date Format' : ''}
                          error={startDateError}
                        />
                        <KeyboardDatePicker
                          className={classes.datePicker}
                          // margin="normal"
                          format={constants.MM_DD_YYYY_FORMAT}
                          autoOk
                          variant="inline"
                          size="small"
                          style={{ minWidth: '32%' }}
                          inputVariant="outlined"
                          disableFuture
                          value={searchFields.endDate || null}
                          label="End Date"
                          minDate={searchFields.startDate || undefined}
                          onChange={(date) => this.handleDateChange(date, 'endDate')}
                          disabled={endDisabled}
                          KeyboardButtonProps={{
                            'aria-label': 'change date'
                          }}
                          helperText={endDateError ? 'Invalid Date Format' : ''}
                          error={endDateError}
                        />
                      </ThemeProvider>
                    </MuiPickersUtilsProvider>
                  </Box>
                  <br />
                </Grid>
                <Grid item xs={2} />
              </Grid>
            )}
            <Grid container direction="row">
              <Grid item xs={8} style={{ marginLeft: '6%' }}>
                <Box
                  sx={{ pl: '10%', ml: '20%' }}
                  style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}
                >
                  <Typography style={{ paddingBottom: '10px', fontWeight: '600', fontSize: '15px' }}>
                    Report By:
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={toggleBtnValue}
                    exclusive
                    onChange={this.handleToggleBtnChange}
                    aria-label="Platform"
                  >
                    <ToggleButton value="cluster">{constants.CLUSTERING}</ToggleButton>
                    <ToggleButton value="count">{constants.COUNT}</ToggleButton>
                    <ToggleButton value="userFeedBack">User Feedback Chart</ToggleButton>
                    <ToggleButton value="blockedCount">Blocked Count Chart</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
              <Grid item xs={6} />
            </Grid>
            <br />
            <div className={chatgptUtilityTrendsStatus.isPending ? classes.blurStyle : null} />
          </Box>
          <Box>
            <Grid container direction="row" justifyContent="center">
              <FormControl size="small">
                <FormControlLabel
                  control={
                    <Checkbox onChange={this.handleFilterUserChange} name="excludeUserId" checked={isUserIdExcluded} />
                  }
                  style={{
                    alignItems: 'center'
                  }}
                  labelPlacement="end"
                  label="Filter Internal Users"
                />
              </FormControl>
            </Grid>
          </Box>
          <br />
          <Box>
            <Grid container justify="space-evenly" className={classes.buttonGroupStyle}>
              <ThemeProvider theme={themes}>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={() => this.handleSearch()}
                  className={classes.buttonStyle}
                >
                  {constants.CHAT_SEARCH}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={this.handleReset}
                  className={classes.buttonStyle}
                >
                  {constants.CHAT_RESET}
                </Button>
              </ThemeProvider>
            </Grid>
            <br />
          </Box>
        </Paper>
        <LoadingHandler
          loading={
            chatgptUtilityTrendsStatus.isPending ||
            chatListStatus.isPending ||
            AIChartsUtilityService.isPending ||
            AIContentFilterChartsService.isPending ||
            aiFeedbackChartsDetailsListService.isPending ||
            AIContentFilterChartsDetailsService.isPending
          }
          isError={
            (chatgptUtilityTrendsStatus.isError || chatListStatus.isError) &&
            (chatgptUtilityTrendsStatus.error || chatListStatus.isError) &&
            (chatgptUtilityTrendsStatus.error === 401 ||
              chatListStatus.error === 401 ||
              chatListStatus.error === null) &&
            (chatgptUtilityTrendsStatus.error === 500 || chatListStatus.error === 500 || chatListStatus.error === null)
          }
          content={
            (gptResponse && gptResponse.length > 0) ||
            (gptListingResponse.chatListDatas.data && gptListingResponse.chatListDatas.data.length > 0) ? (
              <Box>
                <br />
                <ChatTrendsGrid
                  chatgptUtilityTrendsData={gptResponse}
                  chatgptUtilityListingData={gptListingResponse}
                  sorting={this.sorting}
                  setOrder={setOrder}
                  setDirectionValue={setDirectionValue}
                  selectRadioOption={selectRadioOption}
                  selectListingOption={selectListingOption}
                  startDate={searchFields.startDate || ''}
                  endDate={searchFields.endDate || ''}
                  isUserIdExcluded={isUserIdExcluded}
                  tenantId={utils.getTenantId('aistudytools', tenantConfig)}
                  isAllBooks={searchFields?.bookId}
                />
              </Box>
            ) : (
              <>
                <br />
                {['count', 'cluster'].includes(toggleBtnValue) ? (
                  <Paper>
                    <br />
                    <Box>
                      <br />
                      <Typography
                        variant="h4"
                        align="center"
                        gutterBottom
                        spacing={9}
                        className={classes.title}
                        style={{ p: 5, mt: 25 }}
                      >
                        {constants.CHAT_FETCH}
                      </Typography>
                    </Box>
                  </Paper>
                ) : (
                  <></>
                )}
              </>
            )
          }
          loadingContent={<Loader loaderStatus={loaderStatus} />}
          // errorContent={
          //   <StateErrorDisplay error={chatgptUtilityTrendsStatus.error || chatListStatus.error} showDetails />
          // }
        />
        {showCharts.userFeedBackChart && (
          <AIChartsComponent
            searchFields={searchFields}
            handleOnSearch={this.handleOnSearch}
            isSearchClicked={isSearchClicked}
            isUserIdExcluded={isUserIdExcluded}
            tenantName={tenantName}
          />
        )}
        {showCharts.blockedCountChart && (
          <AIContentFilterCharts
            searchFields={searchFields}
            handleOnSearch={this.handleOnSearch}
            isSearchClicked={isSearchClicked}
            bookList={bookList}
            isUserIdExcluded={isUserIdExcluded}
            tenantName={tenantName}
          />
        )}
      </>
    );
  }
}

export default withStyles(styles)(ChatGptUtilityTrends);
